import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import type {
  PopularSearchAdminRow,
  PopularSearchCreateInput,
  PopularSearchMergedItem,
  PopularSearchUpdateInput,
} from '../types/popularSearch';

interface Envelope<T> {
  readonly success: boolean;
  readonly data: T | null;
}

const ENDPOINT = '/admin/v1/search/popular';

/**
 * 공개 병합 엔드포인트 — 운영자 지정(MANUAL) + 자동 집계(AUTO) 통합 결과.
 * storefront 가 실제로 노출하는 리스트와 동일. 신규 admin endpoint 아님.
 */
const PUBLIC_ENDPOINT = '/api/v1/catalog/search/popular';

const fetchPopular = async (): Promise<readonly PopularSearchAdminRow[]> => {
  const res = await adminFetch(ENDPOINT, {
    method: 'GET',
  });
  if (!res.ok) throw new Error(`popular search load failed: ${res.status}`);
  const env = (await res.json()) as Envelope<readonly PopularSearchAdminRow[]>;
  return env.data ?? [];
};

export const popularSearchQueryKey = () => ['admin', 'search', 'popular'] as const;

export const popularSearchMergedQueryKey = () =>
  ['public', 'search', 'popular', 'merged'] as const;

export const useSearchPopularQuery = () =>
  useQuery<readonly PopularSearchAdminRow[]>({
    queryKey: popularSearchQueryKey(),
    queryFn: fetchPopular,
    staleTime: 30_000,
  });

const fetchMergedPopular = async (): Promise<readonly PopularSearchMergedItem[]> => {
  const res = await adminFetch(PUBLIC_ENDPOINT);
  if (!res.ok) throw new Error(`popular search preview load failed: ${res.status}`);
  const env = (await res.json()) as Envelope<readonly PopularSearchMergedItem[]>;
  return env.data ?? [];
};

/** 통합 노출 미리보기(운영자 지정 + 자동 집계 병합). storefront 실노출과 동일. */
export const useSearchPopularMergedQuery = () =>
  useQuery<readonly PopularSearchMergedItem[]>({
    queryKey: popularSearchMergedQueryKey(),
    queryFn: fetchMergedPopular,
    staleTime: 30_000,
  });

const invalidate = (qc: ReturnType<typeof useQueryClient>): Promise<void> =>
  Promise.all([
    qc.invalidateQueries({ queryKey: popularSearchQueryKey() }),
    qc.invalidateQueries({ queryKey: popularSearchMergedQueryKey() }),
  ]).then(() => undefined);

export const useSearchPopularCreateMutation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: PopularSearchCreateInput): Promise<PopularSearchAdminRow> => {
      const res = await adminFetch(ENDPOINT, {
        method: 'POST',
        body: JSON.stringify(input),
      });
      if (!res.ok) throw new Error(`popular search create failed: ${res.status}`);
      const env = (await res.json()) as Envelope<PopularSearchAdminRow>;
      if (!env.data) throw new Error('popular search create returned no data');
      return env.data;
    },
    onSuccess: () => {
      void invalidate(qc);
    },
  });
};

export const useSearchPopularUpdateMutation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: string; input: PopularSearchUpdateInput }) => {
      const res = await adminFetch(`${ENDPOINT}/${encodeURIComponent(vars.id)}`, {
        method: 'PUT',
        body: JSON.stringify(vars.input),
      });
      if (!res.ok) throw new Error(`popular search update failed: ${res.status}`);
      return res.json();
    },
    onSuccess: () => {
      void invalidate(qc);
    },
  });
};

export const useSearchPopularDeleteMutation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await adminFetch(`${ENDPOINT}/${encodeURIComponent(id)}`, {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error(`popular search delete failed: ${res.status}`);
      return res.json();
    },
    onSuccess: () => {
      void invalidate(qc);
    },
  });
};

export const useSearchPopularReorderMutation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids: readonly string[]) => {
      const res = await adminFetch(`${ENDPOINT}/reorder`, {
        method: 'POST',
        body: JSON.stringify({ ids }),
      });
      if (!res.ok) throw new Error(`popular search reorder failed: ${res.status}`);
      return res.json();
    },
    onSuccess: () => {
      void invalidate(qc);
    },
  });
};
